import { useEffect, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import {
  ArrowUturnLeftIcon,
  CursorArrowRaysIcon,
  PencilSquareIcon,
  SquaresPlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { getFieldTypeLabel, LAYOUT_FIELD_TYPES, SEMANTIC_ROLES } from "@/lib/fieldTypeCatalog";
import type { FieldTemplate } from "@/lib/wartungen";
import { fieldSupportsDefectTrigger, isDefectRoleField, resolveDefectTrigger } from "./defectTrigger";
import { getFieldEditorCapabilities, isPureStructureField } from "./fieldEditorCapabilities";
import { getFieldTypeIcon } from "./fieldTypeIcons";
import {
  type BuilderField,
  type BuilderScope,
  fieldsForScope,
  groupIntoRows,
} from "./fieldModel";
import type { DropTarget } from "./useBuilder";
import { beginDrag, endDrag, getDrag } from "./dragState";
import type { PaletteItem } from "./TemplatePalette";

interface BuilderCanvasProps {
  scope: BuilderScope;
  fields: BuilderField[];
  paletteItems: PaletteItem[];
  editingUid: string;
  selectedUid: string;
  dirtyUids: Set<string>;
  onDropTemplate: (template: FieldTemplate, scope: BuilderScope, target: DropTarget | null) => void;
  onMoveField: (uid: string, scope: BuilderScope, target: DropTarget | null) => void;
  onSelect: (uid: string) => void;
  onEdit: (uid: string) => void;
  onReset: (uid: string) => void;
  onDelete: (uid: string) => void;
  renderEditor: (field: BuilderField) => ReactNode;
  emptyHint: string;
}

function scrollFieldCardIntoView(uid: string) {
  requestAnimationFrame(() => {
    document
      .querySelector(`[data-field-uid="${uid}"]`)
      ?.scrollIntoView({ block: "nearest", behavior: "instant" });
  });
}

function getRowCards(rowEl: HTMLElement): HTMLElement[] {
  return Array.from(rowEl.querySelectorAll<HTMLElement>("[data-field-uid]")).filter(
    (entry) => !entry.classList.contains("is-editing") && !entry.closest(".mb-field-card.is-editing"),
  );
}

/** Einfügeindex in einer Zeile (0 = ganz links, rowCards.length = ganz rechts). */
function computeSideInsertCol(rowEl: HTMLElement, clientX: number): number {
  const rowCards = getRowCards(rowEl).sort(
    (a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left,
  );
  if (!rowCards.length) return 0;

  const rowRect = rowEl.getBoundingClientRect();
  if (clientX <= rowRect.left + 8) return 0;

  let col = 0;
  rowCards.forEach((entry, index) => {
    const cardRect = entry.getBoundingClientRect();
    const midX = cardRect.left + cardRect.width / 2;
    if (clientX > midX) col = index + 1;
  });
  return Math.max(0, Math.min(rowCards.length, col));
}

/** Dynamisches Raster: Zeilenbreite = Summe der layout_span-Werte (1–3 Spalten, volle Breite). */
function rowGridLayout(fields: BuilderField[]): { columns: number; cellSpans: number[] } {
  const cellSpans = fields.map((f) => Math.max(1, Math.min(3, Number(f.layout_span) || 1)));
  const columns = Math.max(1, Math.min(3, cellSpans.reduce((sum, span) => sum + span, 0)));
  return {
    columns,
    cellSpans: cellSpans.map((span) => Math.min(span, columns)),
  };
}

function deriveDropTargetFromPointer(
  clientX: number,
  clientY: number,
  scope: BuilderScope,
  rows: BuilderField[][],
): DropTarget | null {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el) return null;

  const rowEl = el.closest(".mb-row") as HTMLElement | null;
  if (!rowEl) return { scope, kind: "end" };

  const cardUids = new Set(
    getRowCards(rowEl).map((card) => card.dataset.fieldUid).filter(Boolean),
  );
  const rowFields = rows.find((row) => row.some((f) => cardUids.has(f.uid)));
  if (!rowFields?.length) return { scope, kind: "end" };

  const card = el.closest("[data-field-uid]") as HTMLElement | null;
  if (card && cardUids.has(card.dataset.fieldUid || "")) {
    const uid = card.dataset.fieldUid!;
    const rect = card.getBoundingClientRect();
    const relY = (clientY - rect.top) / Math.max(1, rect.height);
    if (relY < 0.28) return { scope, kind: "above", anchorUid: uid };
    if (relY > 0.72) return { scope, kind: "below", anchorUid: uid };
  }

  const insertCol = computeSideInsertCol(rowEl, clientX);
  const anchorUid =
    rowFields[Math.min(insertCol, rowFields.length - 1)]?.uid ?? rowFields[0]!.uid;
  return { scope, kind: "side", anchorUid, insertCol };
}

export function BuilderCanvas({
  scope,
  fields,
  paletteItems,
  editingUid,
  selectedUid,
  dirtyUids,
  onDropTemplate,
  onMoveField,
  onSelect,
  onEdit,
  onReset,
  onDelete,
  renderEditor,
  emptyHint,
}: BuilderCanvasProps) {
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);
  const [active, setActive] = useState(false);
  const depth = useRef(0);

  const scoped = fieldsForScope(fields, scope);
  const rows = groupIntoRows(scoped);

  function updateDropTarget(target: DropTarget | null) {
    dropTargetRef.current = target;
    setDropTarget(target);
  }

  function clearDrop() {
    updateDropTarget(null);
    setActive(false);
    depth.current = 0;
  }

  function resolveSideTarget(
    rowEl: HTMLElement,
    rowFields: BuilderField[],
    clientX: number,
  ): DropTarget {
    const insertCol = computeSideInsertCol(rowEl, clientX);
    const anchorUid =
      rowFields[Math.min(insertCol, rowFields.length - 1)]?.uid ?? rowFields[0]!.uid;
    return { scope, kind: "side", anchorUid, insertCol };
  }

  function handleRowDragOver(e: React.DragEvent, rowFields: BuilderField[]) {
    const drag = getDrag();
    if (!drag || drag.kind === "inline-option") return;
    e.preventDefault();
    e.stopPropagation();
    setActive(true);
    updateDropTarget(resolveSideTarget(e.currentTarget as HTMLElement, rowFields, e.clientX));
  }

  function handleCardDragOver(e: React.DragEvent, anchor: BuilderField) {
    const drag = getDrag();
    if (!drag || drag.kind === "inline-option") return;
    e.preventDefault();
    e.stopPropagation();
    setActive(true);
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const relY = (e.clientY - rect.top) / Math.max(1, rect.height);
    let kind: DropTarget["kind"];
    let insertCol: number | undefined;
    if (relY < 0.28) {
      kind = "above";
      updateDropTarget({ scope, kind, anchorUid: anchor.uid });
      return;
    }
    if (relY > 0.72) {
      kind = "below";
      updateDropTarget({ scope, kind, anchorUid: anchor.uid });
      return;
    }

    const rowEl = card.closest(".mb-row") as HTMLElement | null;
    const rowFields = rows.find((row) => row.some((f) => f.uid === anchor.uid)) ?? [anchor];
    if (rowEl) {
      updateDropTarget(resolveSideTarget(rowEl, rowFields, e.clientX));
      return;
    }
    const relX = (e.clientX - rect.left) / Math.max(1, rect.width);
    insertCol = relX < 0.5 ? 0 : 1;
    updateDropTarget({ scope, kind: "side", anchorUid: anchor.uid, insertCol });
  }

  function handleCanvasDragOver(e: React.DragEvent) {
    const drag = getDrag();
    if (!drag || drag.kind === "inline-option") return;
    e.preventDefault();
    setActive(true);
    const rowEl = (e.target as HTMLElement | null)?.closest(".mb-row");
    if (rowEl) return;
    if (!dropTargetRef.current) updateDropTarget({ scope, kind: "end" });
  }

  function handleDrop(e: React.DragEvent) {
    const drag = getDrag();
    if (!drag || drag.kind === "inline-option") return;
    e.preventDefault();
    const target =
      dropTargetRef.current ?? deriveDropTargetFromPointer(e.clientX, e.clientY, scope, rows);
    if (drag.kind === "template") {
      const item = paletteItems.find((p) => p.id === drag.templateId);
      if (item) onDropTemplate(item.template, scope, target);
    } else if (drag.kind === "field" && drag.fieldUid) {
      // Feld nur im selben Scope verschieben.
      if (drag.scope === scope) onMoveField(drag.fieldUid, scope, target);
    }
    endDrag();
    clearDrop();
  }

  // Horizontaler Indikator-Slot (above/below/end).
  const indicatorBeforeRow = (rowIdx: number): boolean => {
    if (!dropTarget) return false;
    if (dropTarget.kind === "above") {
      const anchor = scoped.find((f) => f.uid === dropTarget.anchorUid);
      return anchor != null && rowIdx === rowIndexOf(rows, anchor);
    }
    return false;
  };
  const indicatorAfterRow = (rowIdx: number): boolean => {
    if (!dropTarget) return false;
    if (dropTarget.kind === "below") {
      const anchor = scoped.find((f) => f.uid === dropTarget.anchorUid);
      return anchor != null && rowIdx === rowIndexOf(rows, anchor);
    }
    if (dropTarget.kind === "end") return rowIdx === rows.length - 1;
    return false;
  };

  return (
    <section
      className={clsx("mb-canvas", active && "is-drop-active", depth.current > 0 && "is-drop-hover")}
      aria-label={`Builder-Zeichenfläche ${scope}`}
      onDragEnter={(e) => {
        const drag = getDrag();
        if (!drag || drag.kind === "inline-option") return;
        e.preventDefault();
        depth.current += 1;
      }}
      onDragOver={handleCanvasDragOver}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) {
          setActive(false);
          setDropTarget(null);
        }
      }}
      onDrop={handleDrop}
    >
      {rows.length === 0 ? (
        <>
          {dropTarget ? <DropLine label="Hier ablegen" /> : null}
          <div className="mb-canvas__empty">
            <SquaresPlusIcon className="mb-canvas__empty-icon" aria-hidden />
            <p className="mb-canvas__empty-title">{emptyHint || "Noch keine Felder."}</p>
            <p className="mb-canvas__empty-text">
              Ziehe ein Feld aus der Bibliothek rechts hierher – oder klicke dort auf „+“.
            </p>
          </div>
        </>
      ) : (
        rows.map((row, rowIdx) => {
          const editingInRow = row.find((f) => f.uid === editingUid);
          const visibleFields = editingInRow ? [editingInRow] : row;
          const { columns: rowColumns, cellSpans } = rowGridLayout(visibleFields);
          const rowHasSideDrop =
            dropTarget?.kind === "side" &&
            row.some((f) => f.uid === dropTarget.anchorUid);
          const sideSlotCount = Math.max(visibleFields.length, dropTarget?.insertCol ?? 0, 1);
          const sideInsertPct =
            rowHasSideDrop && dropTarget?.insertCol != null
              ? `${(Math.max(0, dropTarget.insertCol) / sideSlotCount) * 100}%`
              : null;
          return (
            <div key={`row-${rowIdx}`}>
              {indicatorBeforeRow(rowIdx) ? <DropLine label="Oberhalb ablegen" /> : null}
              <div
                className={clsx("mb-row", rowHasSideDrop && "is-side-drop-target")}
                style={{ gridTemplateColumns: `repeat(${rowColumns}, minmax(0, 1fr))` }}
                onDragOver={(e) => handleRowDragOver(e, row)}
              >
                {sideInsertPct != null ? (
                  <span className="mb-row-side-indicator" style={{ left: sideInsertPct }} />
                ) : null}
                {visibleFields.map((field, fieldIdx) => {
                  const isEditing = field.uid === editingUid;
                  const cellSpan = cellSpans[fieldIdx] ?? 1;
                  return (
                    <div
                      key={field.uid}
                      className={clsx("mb-cell", isEditing && "mb-cell--editing")}
                      style={{
                        gridColumn: isEditing ? "1 / -1" : `span ${cellSpan}`,
                      }}
                    >
                      <FieldCard
                        field={field}
                        scope={scope}
                        isEditing={isEditing}
                        isSelected={field.uid === selectedUid}
                        isDirty={dirtyUids.has(field.uid)}
                        onSelect={onSelect}
                        onEdit={onEdit}
                        onReset={onReset}
                        onDelete={onDelete}
                        onCardDragOver={handleCardDragOver}
                        renderEditor={isEditing ? () => renderEditor(field) : undefined}
                      />
                    </div>
                  );
                })}
              </div>
              {indicatorAfterRow(rowIdx) ? <DropLine label="Unterhalb ablegen" /> : null}
            </div>
          );
        })
      )}
      {rows.length > 0 ? (
        <p className="mb-canvas__hint">
          <CursorArrowRaysIcon aria-hidden />
          Felder per Drag &amp; Drop anordnen – nebeneinander gezogen teilen sie sich eine Zeile.
        </p>
      ) : null}
    </section>
  );
}

function rowIndexOf(rows: BuilderField[][], field: BuilderField): number {
  return rows.findIndex((row) => row.some((f) => f.uid === field.uid));
}

function DropLine({ label }: { label: string }) {
  return (
    <div className="mb-drop-indicator" aria-hidden>
      <span className="mb-drop-indicator__label">{label}</span>
    </div>
  );
}

function FieldCard({
  field,
  scope,
  isEditing,
  isSelected,
  isDirty,
  onSelect,
  onEdit,
  onReset,
  onDelete,
  onCardDragOver,
  renderEditor,
}: {
  field: BuilderField;
  scope: BuilderScope;
  isEditing: boolean;
  isSelected: boolean;
  isDirty: boolean;
  onSelect: (uid: string) => void;
  onEdit: (uid: string) => void;
  onReset: (uid: string) => void;
  onDelete: (uid: string) => void;
  onCardDragOver: (e: React.DragEvent, anchor: BuilderField) => void;
  renderEditor?: () => ReactNode;
}) {
  const isLayoutField = LAYOUT_FIELD_TYPES.has(field.type);
  const isPureStructure = isPureStructureField(field.type);
  const caps = getFieldEditorCapabilities(field.type);
  const TypeIcon = getFieldTypeIcon(field.type);
  const displayTitle = isPureStructure
    ? getFieldTypeLabel(field.type)
    : field.label || field.field_key || "(ohne Bezeichnung)";
  const isDefect = scope === "inspection" && caps.semanticRole && isDefectRoleField(field);
  const defectIssue: string | null = !isDefect
    ? null
    : !fieldSupportsDefectTrigger(field.type)
      ? "Rolle passt nicht"
      : resolveDefectTrigger(field)
        ? null
        : "Mangel-Regel fehlt";
  const roleLabel = SEMANTIC_ROLES.find((r) => r.value === field.semantic_role)?.label || field.semantic_role;

  useEffect(() => {
    if (!isEditing) return;
    scrollFieldCardIntoView(field.uid);
  }, [field.uid, isEditing]);

  return (
    <article
      data-field-uid={field.uid}
      className={clsx(
        "mb-field-card",
        isEditing && "is-editing",
        isSelected && "is-selected",
        isDirty && "is-dirty",
      )}
      draggable={!isEditing}
      onClick={() => onSelect(field.uid)}
      onDragStart={(e) => {
        if (isEditing) {
          e.preventDefault();
          return;
        }
        beginDrag({ kind: "field", scope, fieldUid: field.uid });
        e.dataTransfer.setData("application/x-maintenance-drag-type", "field");
        e.dataTransfer.setData("application/x-maintenance-field-uid", field.uid);
        e.dataTransfer.effectAllowed = "move";
        e.currentTarget.classList.add("is-drag-ghost");
      }}
      onDragEnd={(e) => {
        endDrag();
        e.currentTarget.classList.remove("is-drag-ghost");
      }}
      onDragOver={(e) => onCardDragOver(e, field)}
    >
      <div className="mb-field-card__header">
        <span className="mb-field-card__type-icon" title={getFieldTypeLabel(field.type)} aria-hidden>
          <TypeIcon />
        </span>
        <div className="mb-field-card__heading">
          <h4 className="mb-field-card__title">{displayTitle}</h4>
          <div className="mb-field-card__meta">
            {!isPureStructure ? <span className="mb-badge">{getFieldTypeLabel(field.type)}</span> : null}
            {caps.category && field.category ? <span className="mb-badge">{field.category}</span> : null}
            {caps.required && !isLayoutField && field.required ? (
              <span className="mb-badge mb-badge--warn">Pflicht</span>
            ) : null}
            {scope === "inspection" && caps.semanticRole && field.semantic_role !== "info" ? (
              <span className={clsx("mb-badge", isDefect && "mb-badge--defect")}>{roleLabel}</span>
            ) : null}
            {defectIssue && !isEditing ? (
              <span className="mb-badge mb-badge--error" title="Dieses Feld blockiert das Publizieren – Stift-Symbol öffnet die Mangelbewertung.">
                {defectIssue}
              </span>
            ) : null}
            {isDirty ? <span className="mb-badge mb-badge--dirty">geändert</span> : null}
          </div>
        </div>
        {!isEditing ? (
          <div className="mb-field-card__actions">
            <button
              type="button"
              className="mb-icon-btn"
              title="Feld bearbeiten"
              aria-label="Feld bearbeiten"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(field.uid);
              }}
            >
              <PencilSquareIcon aria-hidden />
            </button>
            <button
              type="button"
              className="mb-icon-btn"
              title="Änderungen zurücksetzen"
              aria-label="Änderungen zurücksetzen"
              onClick={(e) => {
                e.stopPropagation();
                onReset(field.uid);
              }}
            >
              <ArrowUturnLeftIcon aria-hidden />
            </button>
            <button
              type="button"
              className="mb-icon-btn mb-icon-btn--danger"
              title="Feld löschen"
              aria-label="Feld löschen"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(field.uid);
              }}
            >
              <TrashIcon aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
      {isEditing && renderEditor ? (
        <div className="mb-field-card__editor" onClick={(e) => e.stopPropagation()}>
          {renderEditor()}
        </div>
      ) : null}
    </article>
  );
}
